import { useMutation, useQuery } from '@apollo/client'
import { Autocomplete, Box, TextField } from '@mui/material'
import { useGridApiContext } from '@mui/x-data-grid-pro'
import { ProcessStatus } from '__generated__/graphql'
import { type FC, useState } from 'react'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import { GetRosterSearchV2 } from 'recruiter/contexts'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { UpdateProcessStatus } from 'recruiter/graphql/updateProcessStatus.gql'
import useToast from 'shared/components/Toast/useToast'
import {
    formatGraphQLError,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'
import { type RosterTableRowModel } from './peopleRosterColumns'

const PeopleProcessStatusCell: FC<{ row: RosterTableRowModel }> = ({ row }) => {
    const [editing, setEditing] = useState(false)
    const toast = useToast()
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)
    const gridApi = useGridApiContext()

    const rosterId = row.id
    const rosterCandidateId = (row as any).rosterCandidateId as string
    const canEdit = (row as any).canEdit ?? false
    const processStatusId = (row as any).processStatusId as string | null

    const currentProcessStatus =
        processStatusOptions?.processStatuses.find(
            (ps) => ps.id === processStatusId
        ) ?? null

    const [updateProcessStatus] = useMutation(UpdateProcessStatus, {
        refetchQueries: [GetRosterSearchV2],
        onCompleted(data) {
            if (data.updateCandidateProcessStatus.success) {
                toast({
                    title: 'Success',
                    message:
                        data.updateCandidateProcessStatus.message ||
                        'Candidate process status updated successfully',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.updateCandidateProcessStatus.message ||
                        'Error updating process status',
                    severity: 'error',
                })
            }
        },
        onError(error) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating process status'
                ),
                severity: 'error',
            })
        },
    })

    if (!editing) {
        return (
            <Box
                onDoubleClick={() => {
                    if (canEdit) setEditing(true)
                }}
                sx={{
                    cursor: 'default',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                }}
            >
                <ProcessStatusPill processStatusId={currentProcessStatus?.id} />
            </Box>
        )
    }

    return (
        <Autocomplete
            open
            openOnFocus
            onClose={() => setEditing(false)}
            onBlur={() => setEditing(false)}
            onChange={(_, processStatus) => {
                setEditing(false)
                const timestamp = new Date().toISOString()

                updateProcessStatus({
                    variables: {
                        rosterId,
                        rosterCandidateIds: [rosterCandidateId],
                        processStatus:
                            processStatus?.status ?? ProcessStatus.NoStatus,
                    },
                    onCompleted: () => {
                        /* 🔸 patch only this row in the grid */
                        const currentRowId = rosterId?.toString()
                        if (currentRowId && gridApi?.current?.getRow) {
                            const existingRow: any =
                                gridApi.current.getRow(currentRowId)
                            if (existingRow) {
                                const updatedRow = {
                                    ...existingRow,
                                    processStatusId: processStatus?.id ?? null,
                                    processStatusLabel:
                                        processStatus?.statusLabel ?? null,
                                    processStatusSummaryGroup:
                                        processStatus?.processStatusSummaryGroup
                                            ?.label ?? null,
                                    processStatusUpdatedAt: timestamp,
                                }
                                gridApi.current.updateRows([updatedRow])
                            }
                        }
                    },
                })
            }}
            options={sortProcessStatusesBySummaryGroup(
                processStatusOptions?.processStatuses ?? []
            )}
            value={currentProcessStatus ?? undefined}
            getOptionLabel={(option) => option.statusLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            groupBy={(option) => option.processStatusSummaryGroup?.label || ''}
            disableClearable
            fullWidth
            size="small"
            sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    py: 0,
                    '& fieldset': { borderColor: 'divider' },
                    '&:hover fieldset': { borderColor: 'text.secondary' },
                },
            }}
            renderOption={({ key, ...optionProps }, option, { selected }) => (
                <li
                    key={key}
                    {...optionProps}
                    style={{
                        fontSize: '0.75rem',
                        paddingTop: 4,
                        paddingBottom: 4,
                        minHeight: 'unset',
                        backgroundColor: selected
                            ? 'rgba(25, 118, 210, 0.08)'
                            : undefined,
                        borderLeft: selected ? '3px solid #1976d2' : 'none',
                    }}
                >
                    {option.statusLabel}
                </li>
            )}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-groupLabel': {
                            fontSize: '0.6875rem',
                            lineHeight: 2,
                            fontWeight: 600,
                        },
                    },
                },
            }}
            renderInput={(renderParams) => (
                <TextField {...renderParams} fullWidth autoFocus />
            )}
        />
    )
}

export default PeopleProcessStatusCell
